import React from 'react';
import { Typography, Tag, Row, Col, Tabs, Button, Flex, Space, Modal, Card, Progress } from 'antd';
import {
  UserOutlined,
  CalendarOutlined,
  PushpinFilled,
  EditOutlined,
  ImportOutlined,
  PlusOutlined,
  BarChartOutlined,
  RiseOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';

const metrics = [
  { value: 0, label: 'Target Families' },
  { value: 0, label: 'Completed Responses', color: '#27ae60' },
  { value: '0%', label: 'Response Rate', color: '#8e44ad' },
];

export const AuditDetails = ({ audit, onClose, isOpen }) => {
  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      width={800}
      footer={null}
      title={
        <Flex align="center" gap={16}>
          <Typography.Title level={4} style={{ margin: 0 }}>
            {audit?.title}
          </Typography.Title>
          <Tag color="#fff7d6" style={{ color: '#b38600', fontWeight: 500, border: 'none' }}>
            {audit?.type}
          </Tag>
        </Flex>
      }>
      <Flex align="center" justify="space-between" style={{ marginBottom: 8 }}>
        <Typography.Text type="secondary">
          Understanding family satisfaction and likelihood to return next year
        </Typography.Text>
        <Space size={8}>
          <Button type="text" icon={<PushpinFilled />} />
          <Button type="text" icon={<EditOutlined />} />
        </Space>
      </Flex>
      <Row gutter={24} style={{ margin: '16px 0' }}>
        <Col>
          <Typography.Text type="secondary">
            <UserOutlined style={{ marginRight: 4 }} /> School:
          </Typography.Text>
          <Typography.Text strong style={{ marginLeft: 4 }}>
            {audit?.school}
          </Typography.Text>
        </Col>
        <Col>
          <Typography.Text type="secondary">
            <CalendarOutlined style={{ marginRight: 4 }} /> Created:
          </Typography.Text>
          <Typography.Text strong style={{ marginLeft: 4 }}>
            {audit?.date}
          </Typography.Text>
        </Col>
        <Col>
          <Typography.Text type="secondary">Type:</Typography.Text>
          <Typography.Text strong style={{ marginLeft: 4 }}>
            {audit?.type}
          </Typography.Text>
        </Col>
      </Row>
      <Row
        gutter={16}
        style={{ background: '#f7faff', borderRadius: 12, padding: '16px 0', marginBottom: 16 }}
        align="middle"
        justify="center">
        {metrics.map((m, i) => (
          <Col key={i} span={8} style={{ textAlign: 'center' }}>
            <Typography.Title level={3} style={{ margin: 0, color: m.color }}>
              {m.value}
            </Typography.Title>
            <Typography.Text type="secondary">{m.label}</Typography.Text>
          </Col>
        ))}
      </Row>
      <Tabs
        defaultActiveKey="families"
        items={[
          {
            key: 'families',
            label: 'Families',
            children: (
              <Flex vertical align="center" justify="center" style={{ minHeight: 180, marginTop: 24 }}>
                <UserOutlined style={{ fontSize: 48, color: '#b0b7c3', marginBottom: 16 }} />
                <Typography.Title level={5} style={{ margin: 0 }}>
                  No Families Assigned
                </Typography.Title>
                <Typography.Text type="secondary">This audit doesn't have any families assigned yet.</Typography.Text>
                <Button type="primary" icon={<ImportOutlined />} style={{ marginTop: 16 }}>
                  Import Families
                </Button>
              </Flex>
            ),
          },
          {
            key: 'questions',
            label: 'Questions',
            children: (
              <Flex vertical align="center" justify="center" style={{ minHeight: 180, marginTop: 24 }}>
                <UserOutlined style={{ fontSize: 48, color: '#b0b7c3', marginBottom: 16 }} />
                <Typography.Title level={5} style={{ margin: 0 }}>
                  No Questions Found
                </Typography.Title>
                <Typography.Text type="secondary">This audit doesn't have any questions yet.</Typography.Text>
                <Button type="primary" icon={<PlusOutlined />} style={{ marginTop: 16 }}>
                  Add Questions
                </Button>
              </Flex>
            ),
          },
          {
            key: 'reports',
            label: 'Reports & Analytics',
            children: (
              <div>
                <Typography.Title level={4} style={{ marginBottom: 0 }}>
                  Reports & Analytics
                </Typography.Title>
                <Typography.Text type="secondary" style={{ fontSize: 16 }}>
                  Professional reports and insights from audit responses
                </Typography.Text>
                <Row gutter={16} style={{ margin: '24px 0' }}>
                  <Col span={6}>
                    <ReportCard report="Total Families" amount={0} Icon={UserOutlined} />
                  </Col>
                  <Col span={6}>
                    <ReportCard report="Completed" amount={0} Icon={CheckCircleOutlined} />
                  </Col>
                  <Col span={6}>
                    <ReportCard report="Response Rate" amount="0%" Icon={RiseOutlined} />
                  </Col>
                  <Col span={6}>
                    <ReportCard report="Avg Sentiment" amount="0.0" Icon={BarChartOutlined} />
                  </Col>
                </Row>
                <Card bordered style={{ borderRadius: 12, marginBottom: 24 }}>
                  <Typography.Title level={5} style={{ marginBottom: 0 }}>
                    <span style={{ marginRight: 8, fontSize: 20 }}>◎</span> Audit Progress
                  </Typography.Title>
                  <Typography.Text type="secondary">Response Collection</Typography.Text>
                  <Flex align="center" gap={24} style={{ margin: '16px 0 8px 0' }}>
                    <Progress
                      style={{ flex: 1 }}
                      percent={50}
                      showInfo={false}
                      strokeColor="#006b8f"
                      trailColor="#e6eaf1"
                      size="small"
                    />
                    <Typography.Text type="secondary">9 of 15</Typography.Text>
                  </Flex>
                  <Flex align="center" justify="space-between" style={{ marginTop: 8 }}>
                    <div style={{ textAlign: 'center', flex: 1 }}>
                      <Typography.Title level={4} style={{ color: '#27ae60', margin: 0 }}>
                        9
                      </Typography.Title>
                      <Typography.Text type="secondary">Families Responded</Typography.Text>
                    </div>
                    <div style={{ textAlign: 'center', flex: 1 }}>
                      <Typography.Title level={4} style={{ color: '#222', margin: 0 }}>
                        6
                      </Typography.Title>
                      <Typography.Text type="secondary">Pending Responses</Typography.Text>
                    </div>
                  </Flex>
                </Card>
                <Card bordered style={{ background: '#fffbe6', borderColor: '#ffe58f', borderRadius: 12 }}>
                  <Flex align="center" gap={12}>
                    <ClockCircleOutlined />
                    <div>
                      <Typography.Text strong style={{ color: '#b38600' }}>
                        Data Collection in Progress
                      </Typography.Text>
                      <br />
                      <Typography.Text type="secondary">
                        Reports will be available once the audit reaches completion status. Current progress: 60.0%
                        complete.
                      </Typography.Text>
                    </div>
                  </Flex>
                </Card>
              </div>
            ),
          },
        ]}
      />
    </Modal>
  );
};

const ReportCard = ({ report, amount, Icon }) => {
  return (
    <Card bordered size="small" style={{ borderRadius: 12 }}>
      <Flex align="center" gap={16}>
        <Icon style={{ fontSize: 24, color: '#006b8f', marginBottom: 8 }} />
        <div>
          <Typography.Text type="secondary">{report}</Typography.Text>
          <Typography.Title level={5} style={{ margin: 0 }}>
            {amount}
          </Typography.Title>
        </div>
      </Flex>
    </Card>
  );
};
